<template>
	<div>
		<PageHeader
			:data="{
				name: carnivore.name,
				breadcrumbs: [
					{ name: 'Home', to: '/' },
					{ name: 'Carnivores', to: '/carnivores' },
				],
			}"
		/>

		<div class="row">
			<!-- Progress -->
			<div class="col-12 mb-2">
				<div class="card">
					<div
						class="card-body d-flex justify-content-between p-3 rounded"
						:class="{
							'bg-success': carnivore.status === 'finished',
							'bg-primary': carnivore.status === 'progress',
							'text-white': carnivore.status === 'progress',
						}"
					>
						<h5 class="text-uppercase m-0 p-0">{{ carnivore.status }}</h5>
						<!-- Progress Bar -->
						<div class="w-50 d-flex">
							<div class="progress" style="height: 25px;width:85%">
								<div
									class="progress-bar progress-bar-striped bg-success fs-6"
									role="progressbar"
									:style="'width:' + progress.percent + '%'"
									:aria-valuenow="progress.percent"
									aria-valuemin="0"
									aria-valuemax="100"
								>
									{{ progress.percent }} %
								</div>
							</div>
							<span class="ms-2">{{ progress.finished }} / {{ progress.total }}</span>
						</div>

						<!-- #Progress Bar -->
					</div>
				</div>
			</div>
			<!-- #Progress -->

			<!-- Edit Carnivore data -->
			<div class="col-md-12 mb-2">
				<div class="card">
					<div class="card-body">
						<form @submit.prevent="updateCarnivore">
							<div class="row">
								<!-- Carnivore main info -->
								<div class="col-md-6">
									<div class="form-group">
										<label for="">Carnivore Name <code>*</code></label>
										<div class="input-group">
											<input type="text" class="form-control" placeholder="Carnivore name" v-model="carnivore.name" required />
										</div>
									</div>
									<div class="form-group">
										<label for="">Description</label>
										<div class="input-group">
											<textarea class="form-control" placeholder="Carnivore description" v-model="carnivore.description" />
										</div>
									</div>
									<div class="form-group">
										<label for="">Priority</label>
										<select class="form-select" v-model="carnivore.priority">
											<option value="normal">Normal</option>
											<option value="medium">Medium</option>
											<option value="high">High</option>
										</select>
									</div>

									<div class="form-group">
										<label for="">Choose workers <code>*</code> </label>

										<multiselect
											:searchable="true"
											v-model="carnivore.workers"
											:options="entryWorkers"
											track-by="name"
											label="name"
											:multiple="true"
											required
										></multiselect>
										<small>ℹ️ If workers are not selected no one will be able to do data entry</small>
									</div>
								</div>
								<!-- #Carnivore main info -->
								<!-- Carnivore fields to add -->
								<div class="col-md-6">
									<div class="row">
										<div class="col-md-12 d-flex justify-content-between align-items-center mb-3">
											<span>Choose which fields need to add in carnivore <code>*</code></span>
											<div class="form-check form-switch d-flex align-items-center">
												<input 
													class="form-check-input" 
													type="checkbox" 
													role="switch" 
													id="aiDataWorkerToggle"
													v-model="showDataWorkerMode"
												/>
												<label class="form-check-label ms-2 mb-0" for="aiDataWorkerToggle">
													<small>AI Data Worker Mode</small>
												</label>
											</div>
										</div>
										<div v-if="showDataWorkerMode" class="col-md-12">
											<div class="form-check">
												<div v-for="field in carnivore.fields" :key="field.name">
													<div v-if="field.name === 'contacts' && showDataWorkerMode" class="mb-3">
														<div class="d-flex align-items-center gap-3 mb-2">
															<div class="d-flex align-items-center">
																<input
																	class="form-check-input"
																	type="checkbox"
																	v-model="field.active"
																	:id="field.name"
																	:disabled="carnivore.data !== null && !toggleFieldsEdit"
																/>
																<label class="form-check-label ms-2" :for="field.name">
																	{{ field.label }}
																</label>
															</div>
															<div class="flex-grow-1" v-if="showDataWorkerMode">
																<input 
																	type="text" 
																	class="form-control form-control-sm" 
																	v-model="field.description" 
																	placeholder="Enter custom description"
																	:disabled="!field.active"
																/>
															</div>
														</div>
														<!-- Contact types section -->
														<div v-if="field.active" class="mt-2">
															<div class="d-flex justify-content-between align-items-center mb-2">
																<small class="text-muted">Contact Types (AI Data Worker Only):</small>
																<button 
																	type="button" 
																	class="btn btn-sm btn-outline-primary" 
																	@click="addContactType(field)"
																	:disabled="!field.active"
																>
																	<font-awesome-icon :icon="['fas', 'plus']" />
																</button>
															</div>
															
															<div class="d-flex flex-wrap gap-2">
																<div v-for="(contactType, index) in field.contactTypes" :key="index" class="contact-type-section p-2 border rounded flex-grow-1" style="min-width: 200px;">
																	<div class="d-flex gap-2 mb-2">
																		<div class="flex-grow-1">
																			<input 
																				type="text" 
																				class="form-control form-control-sm" 
																				:value="contactType.type"
																				@input="updateContactType(field, index, $event.target.value)"
																				placeholder="Type (e.g. mayor)"
																				:disabled="!field.active"
																			/>
																		</div>
																		<div class="flex-grow-1">
																			<input 
																				type="text" 
																				class="form-control form-control-sm" 
																				v-model="contactType.description" 
																				placeholder="Description"
																				:disabled="!field.active"
																			/>
																		</div>
																		<button 
																			type="button" 
																			class="btn btn-sm btn-outline-danger" 
																			@click="removeContactType(field, index)"
																			:disabled="!field.active"
																		>
																			×
																		</button>
																	</div>
																	<div class="contact-fields d-flex flex-wrap gap-2 align-items-center">
																		<div class="form-check form-check-inline me-2">
																			<input class="form-check-input" type="checkbox" v-model="contactType.fields.firstName" :id="'firstName-' + index" :disabled="!field.active">
																			<label class="form-check-label" :for="'firstName-' + index">First Name</label>
																		</div>
																		<div class="form-check form-check-inline me-2">
																			<input class="form-check-input" type="checkbox" v-model="contactType.fields.lastName" :id="'lastName-' + index" :disabled="!field.active">
																			<label class="form-check-label" :for="'lastName-' + index">Last Name</label>
																		</div>
																		<div class="form-check form-check-inline me-2">
																			<input class="form-check-input" type="checkbox" v-model="contactType.fields.email" :id="'email-' + index" :disabled="!field.active">
																			<label class="form-check-label" :for="'email-' + index">Email</label>
																		</div>
																		<div class="form-check form-check-inline me-2">
																			<input class="form-check-input" type="checkbox" v-model="contactType.fields.phone" :id="'phone-' + index" :disabled="!field.active">
																			<label class="form-check-label" :for="'phone-' + index">Phone</label>
																		</div>
																		<div class="form-check form-check-inline">
																			<input class="form-check-input" type="checkbox" v-model="contactType.fields.title" :id="'title-' + index" :disabled="!field.active">
																			<label class="form-check-label" :for="'title-' + index">Title (fields to extract)</label>
																		</div>
																	</div>
																</div>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
										<div class="col-md-12">
											<div class="row mt-3">
												<div :class="showDataWorkerMode ? 'col-12' : 'col-6'" v-for="field in carnivore.fields" :key="field.name">
													<div v-if="field.name !== 'contacts' || !showDataWorkerMode" class="form-check mb-2 d-flex align-items-center gap-3">
														<div class="d-flex align-items-center">
															<input
																class="form-check-input"
																type="checkbox"
																v-model="field.active"
																:id="field.name"
																:disabled="carnivore.data !== null && !toggleFieldsEdit"
															/>
															<label class="form-check-label ms-2" :for="field.name">
																{{ field.label }}
															</label>
														</div>
														<div class="flex-grow-1" v-if="showDataWorkerMode">
															<input 
																type="text" 
																class="form-control form-control-sm" 
																v-model="field.description" 
																placeholder="Enter custom description"
																:disabled="!field.active"
															/>
														</div>
													</div>
												</div>
											</div>
										</div>
										<div class="col-md-12">
											<div
												class="form-check form-switch d-flex justify-content-start align-items-center ms-1 mt-3"
												v-if="carnivore.data !== null"
												@click="changeFieldSettings()"
											>
												<input class="form-check-input" type="checkbox" role="switch" v-model="toggleFieldsEdit" checked />
												<label class="form-check-label ms-2" for="flexSwitchCheckChecked">Enable edit</label>
											</div>
										</div>
									</div>
								</div>
								<!-- #Carnivore fields to add -->
								<div class="col-md-12 mt-2">
									<button v-if="!updateLoading" class="btn btn-primary float-end">Update</button>
									<button v-else class="btn btn-primary float-end" type="button" disabled>
										<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
										Updating...
									</button>
								</div>
							</div>
						</form>
					</div>
				</div>
			</div>
			<!-- #Edit Carnivore data -->

			<!-- Show user statistic -->
			<div class="col-md-12 mb-2" v-if="carnivore.workers && carnivore.workers.length > 0">
				<div class="card">
					<div class="card-body">
						<div class="d-flex justify-content-between align-items-center mb-2">
							<h5>Workers statistic <button class="btn btn-sm btn-link" v-if="false">show</button></h5>
							<button class="btn btn-sm btn-outline-danger fw-normal" v-if="false">
								Release all users
							</button>
						</div>
						<div>
							<template v-for="(worker, index) in carnivore.workers">
								<div :key="'worker-stats-' + index" class="d-flex justify-content-between align-items-center py-1">
									<div class="d-flex justify-content-start align-items-center">
										<b>{{ worker.name }}</b> <code class="ms-3">({{ worker.email }})</code>
									</div>

									<div class="w-50 d-flex justify-content-between">
										<div class="statistic">
											<span class="badge bg-success me-1 p-1 px-2">
												Finished:
												<b>{{ statusObj(worker).finished }}</b>
											</span>
											<span class="badge bg-warning me-1 p-1 px-2"
												>Skipped: <b>{{ statusObj(worker).skipped }}</b></span
											>
											<span class="badge bg-primary p-1 px-2"
												>Total: <b>{{ statusObj(worker).total }}</b></span
											>
										</div>

										<!-- Progress Bar -->
										<div class="w-50 d-flex" v-if="workerStats.length > 0">
											<div class="progress" style="height: 25px;width:75%">
												<div
													class="progress-bar progress-bar-striped bg-success fs-6"
													role="progressbar"
													:style="'width:' + statusObj(worker).percent + '%'"
													:aria-valuenow="statusObj(worker).percent"
													aria-valuemin="0"
													aria-valuemax="100"
												>
													{{ statusObj(worker).percent }} %
												</div>
											</div>
											<span class="ms-2" style="width:25%">{{ statusObj(worker).finished }} / {{ statusObj(worker).total }}</span>
										</div>
									</div>
								</div>
							</template>
						</div>
					</div>
				</div>
			</div>

			<!-- AI Data Worker Summary -->
			<div class="col-md-12 mb-2" v-if="carnivore.external_data && carnivore.external_data.data && carnivore.external_data.data.summary">
				<div class="card">
					<div class="card-body">
						<div class="d-flex justify-content-between align-items-center mb-2">
							<h5>AI Data Worker Summary</h5>
						</div>
						<div class="d-flex justify-content-between align-items-center">
							<div class="statistic">
								<span class="badge bg-info me-1 p-1 px-2">
									Total Cost: <b>${{ carnivore.external_data.data.summary.total_cost }}</b>
								</span>
								<span class="badge bg-info p-1 px-2">
									Tokens Used: <b>{{ carnivore.external_data.data.summary.total_tokens }}</b>
								</span>
							</div>
						</div>
					</div>
				</div>
			</div>
			<!-- #AI Data Worker Summary -->

			<!-- Carnivore entries -->
			<div class="col-12 mb-2">
				<div class="card">
					<div class="card-body">
						<div class="d-flex justify-content-between align-items-center">
							<h5>Carnivore entries</h5>
						</div>

						<table class="table table-hover">
							<thead>
								<tr>
									<th>Name</th>
									<th>Finished By</th>
									<th>Current User</th>
									<th width="100">Status</th>
								</tr>
							</thead>
							<tbody>
								<tr @click="displayEntryInfo(entry)" v-for="entry in entries" :key="entry.id">
									<td>{{ entry.municipality.full_name }} , {{ entry.municipality.state }}</td>
									<td>
										<span v-if="entry.status === 'finished'" class="badge bg-secondary text-white fs-7 m-0 p-1 rounded-1">
											{{ entry.worker.name }}
										</span>
										<span v-if="entry.status === 'skipped'" class="badge bg-secondary text-white fs-7 m-0 p-1 rounded-1">
											{{ entry.worker.name }}
										</span>
									</td>
									<td>
										<div v-if="entry.user">
											<span class="badge bg-primary text-white fs-7 m-0 p-1 rounded-1">
												{{ entry.user.name }}
											</span>
											<button class="btn btn-sm btn-link b-0 text-secondary" title="Release municipality" @click="releaseMunicipality(entry)">
												<font-awesome-icon :icon="['fas', 'door-open']" />
											</button>
										</div>
									</td>
									<td>
										<span v-if="entry.status === 'progress'" class="badge badge-primary bg-primary">
											{{ entry.status }}
										</span>
										<span v-if="entry.status === 'finished'" class="badge badge-success bg-success">
											{{ entry.status }}
										</span>
										<span v-if="entry.status === 'skipped'" class="badge badge-warning bg-warning">
											{{ entry.status }}
										</span>
									</td>
								</tr>
							</tbody>
						</table>
						<LPDataTablePagination :pagination="pagination" v-on:getItems="getItems($event.page)" />
					</div>
				</div>
			</div>
			<!-- #Carnivore entries -->

			<!-- Entry info log -->
			<div class="col-md-12" v-if="entryInfo">
				<div class="card">
					<div class="card-body">
						<div class="d-flex justify-content-between align-items-center mb-3">
							<div>
								<h4 v-if="entryInfo.municipality.municipality_full_name">🏛️ {{ entryInfo.municipality.full_name }}</h4>
								<small>Data entry statistic ({{ carnivore.name }})</small>
							</div>

							<button class="btn btn-sm btn-outline-primary" @click="entryInfo = null">close</button>
						</div>

						<div class="row">
							<div class="col-md-6">
								<h5>👴 Old data</h5>
								<template v-for="field in carnivore.fields">
									<div :key="field.name">
										<div v-if="field.name !== 'contacts' && field.active === true">
											<code>
												{{ field.label }}
											</code>
											<p v-if="!entryInfo.old_data.municipality[field.name]" class="p-0 m-0">
												empty
											</p>
											<p v-else class="p-0 m-0">
												{{ entryInfo.old_data.municipality[field.name] }}
											</p>
										</div>
									</div>
								</template>
								<pre v-if="false">
									{{ entryInfo.old_data }}
								</pre
								>
								<div v-if="carnivore.fields.find(f => f.name === 'contacts').active === true" class="mt-3">
									<h6>Contacts</h6>
									<template v-for="(contact, index) in entryInfo.old_data.contacts">
										<div :key="index">
											<span>{{ contact.first_name }} {{ contact.last_name }}</span> | {{ contact.email }} | {{ contact.title }}
										</div>
									</template>
								</div>
							</div>
							<div class="col-md-6">
								<h5>👦 New data</h5>
								<template v-for="field in carnivore.fields">
									<div :key="field.name">
										<div v-if="field.name !== 'contacts' && field.active === true">
											<code>
												{{ field.label }}
											</code>
											<p v-if="!entryInfo.new_data[field.name]" class="p-0 m-0">
												empty
											</p>
											<p v-else class="p-0 m-0">
												{{ entryInfo.new_data[field.name] }}
												<span v-if="entryInfo.new_data[field.name] !== entryInfo.old_data.municipality[field.name]" class="badge bg-success p-1 mx-2 px-2"
													>new</span
												>
											</p>
										</div>
									</div>
								</template>
								<div v-if="carnivore.fields.find(f => f.name === 'contacts').active === true" class="mt-3">
									<h6>Contacts</h6>
									<template v-for="(contact, index) in entryInfo.new_data.contacts">
										<div :key="index">
											<span>{{ contact.first_name }} {{ contact.last_name }}</span> | {{ contact.email }} | {{ contact.title }}
										</div>
									</template>
									<div class="mt-2">
										<span class="badge bg-success" v-if="entryInfo.new_data.contacts.length > entryInfo.old_data.contacts.length"
											>Added
											{{ entryInfo.new_data.contacts.length - entryInfo.old_data.contacts.length }}
											contact/s</span
										>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<!-- #Entry info log -->
		</div>
	</div>
</template>

<script>
import PageHeader from '@/components/layout/PageHeader'
import { mapState } from 'vuex'
import restApi from '../../api/index.js'
import { handleErrors } from '../../actions/helpers'
import LPDataTablePagination from '@/components/lp-data-table/LPDataTablePagination'
export default {
	name: 'CarnivoreEdit',
	components: { PageHeader, LPDataTablePagination },
	data() {
		return {
			updateLoading: false,
			loading: false,
			carnivore: {
				name: 'Carnivore',
			},
			entries: [],
			pagination: {},
			page: this.$route.query.page || 1,
			progress: {
				total: 0,
				finished: 0,
				percent: 0,
			},
			entryWorkers: [],
			entryInfo: null,
			toggleFieldsEdit: false,
			workerStats: [],
			showDataWorkerMode: false,
		}
	},
	computed: {
		...mapState(['authUser', 'storageUrl']),
	},
	created() {
		this.getEntryWorkers()
		this.getCarnivore()
		this.carnivoreProgress()
		const timer = setInterval(() => {
			this.carnivoreProgress()
			this.getCarnivoreMunicipalityEntries(true)
		}, 30000)

		this.$once('hook:beforeDestroy', () => {
			clearInterval(timer)
		})
	},

	methods: {
		// Get Carnivore
		getCarnivore() {
			restApi
				.get(`/carnivores/${this.$route.params.id}`)
				.then(({ data }) => {
					this.carnivore = data
					let fields = JSON.parse(this.carnivore.fields)
					let workers = JSON.parse(this.carnivore.workers)
					this.carnivore.workers = workers
					
					// Ensure contactTypes exists for contacts field
					fields = fields.map(field => {
						if (field.name === 'contacts') {
							if (!field.contactTypes || !Array.isArray(field.contactTypes)) {
								field.contactTypes = [{
									type: '',
									description: '',
									fields: {
										firstName: true,
										lastName: true,
										email: true,
										phone: true,
										title: true
									}
								}]
							}
						}
						return field
					})
					
					this.carnivore.fields = fields
					this.getCarnivoreMunicipalityEntries()
					this.getUsersStats()
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Get carnivore municipality entries
		getCarnivoreMunicipalityEntries(first = false) {
			this.params = {
				page: this.page,
			}
			restApi
				.get(`/carnivores/${this.$route.params.id}/entries`, { params: this.params })
				.then(({ data }) => {
					this.entries = data.data
					this.pagination = data
					delete this.pagination.data
					if (!first) {
						this.$router.push({ path: this.$router.currentPath, query: this.params })
					}
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Get carnivore progress
		carnivoreProgress() {
			restApi
				.post(`/carnivores/${this.$route.params.id}/progress`)
				.then(({ data }) => {
					this.progress = data
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Update carnivore details
		updateCarnivore() {
			this.updateLoading = true
			restApi
				.put(`/carnivores/${this.$route.params.id}`, this.carnivore)
				.then(() => {
					//this.$router.push(`/carnivore/edit/${data.id}`)
					this.updateLoading = false
					this.$swal.fire({
						icon: 'success',
						title: 'Carnivore successfully updated!',
						timer: 4000,
					})
				})
				.catch(error => {
					console.log(error, 'dsdssd')
					this.updateLoading = false
					let errorMessage = handleErrors(error, 'carnivore')
					
					// Try to get more specific error message from the response
					if (error.response && error.response.data) {
						if (error.response.data.message) {
							errorMessage = error.response.data.message
						} else if (error.response.data.error && error.response.data.error.message) {
							errorMessage = error.response.data.error.message
						}
					}
					
					this.$swal.fire({
						icon: 'error',
						title: errorMessage,
						timer: 6000,
					})
				})
		},

		// Get Entry Workers
		getEntryWorkers() {
			restApi
				.get(`/helper/entry-workers`)
				.then(({ data }) => {
					this.entryWorkers = data
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 4000,
					})
				})
		},

		displayEntryInfo(entry) {
			if (entry.status === 'finished') {
				this.entryInfo = entry
				let newData = JSON.parse(entry.new_data)
				let oldData = JSON.parse(entry.old_data)
				this.entryInfo.new_data = newData
				this.entryInfo.old_data = oldData
			}
		},

		releaseMunicipality(entry) {
			this.$swal
				.fire({
					title: 'Are you sure wanna release this municipality from ' + entry.user.name + '?',
					text: `You won't be able to revert this! `,
					icon: 'warning',
					position: 'center',
					showCancelButton: true,
					showConfirmButton: true,
					confirmButtonColor: '#3085d6',
					cancelButtonColor: '#d33',
					confirmButtonText: 'Yes, release it!',
					toast: false,
					timer: 0,
				})
				.then(result => {
					if (result.isConfirmed) {
						entry.user = null
						restApi
							.post(`carnivores/${this.carnivore.id}/release-entry`, { id: entry.id })
							.then(() => {
								entry.user = null
								this.$swal.fire({
									icon: 'success',
									title: `Municipality successfully released from user!`,
								})
							})
							.catch(error => {
								this.$swal.fire({
									icon: 'error',
									title: handleErrors(error, ''),
									timer: 6000,
								})
							})
					}
				})
		},

		changeFieldSettings() {
			if (this.toggleFieldsEdit === false) {
				this.$swal
					.fire({
						title: 'Are you sure wanna enable changing fields for adding data?',
						text: `Note that all finished municipalities will not be affected with this change, this changes will affect only not finished municipalities`,
						icon: 'warning',
						position: 'center',
						showCancelButton: true,
						showConfirmButton: true,
						confirmButtonColor: '#3085d6',
						cancelButtonColor: '#d33',
						confirmButtonText: 'Yes, enable it!',
						toast: false,
						timer: 0,
					})
					.then(result => {
						if (result.isConfirmed) {
							this.toggleFieldsEdit = true
						} else {
							this.toggleFieldsEdit = false
						}
					})
			}
		},

		getUsersStats() {
			restApi
				.post(`/carnivores/${this.$route.params.id}/users-statistic`)
				.then(({ data }) => {
					this.workerStats = data
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		statusObj(worker) {
			let workerObj = this.workerStats.find(w => w.id === worker.id)
			if (workerObj) {
				return {
					finished: workerObj.data.finished,
					skipped: workerObj.data.skipped,
					total: workerObj.data.total,
					percent: workerObj.data.percent,
				}
			} else {
				return { finished: 0, skipped: 0, total: this.pagination.total, percent: 0 }
			}
		},

		// Get items from page that returned from LPDataTablePagination
		getItems(page) {
			this.page = page
			this.getCarnivoreMunicipalityEntries()
		},

		addContactType(field) {
			if (!field.contactTypes) {
				field.contactTypes = []
			}
			const newType = {
				type: '',
				description: '',
				fields: {
					firstName: true,
					lastName: true,
					email: true,
					phone: true,
					title: true
				}
			}
			field.contactTypes.push(newType)
			// Force Vue to update the view
			this.$set(field, 'contactTypes', [...field.contactTypes])
		},

		removeContactType(field, index) {
			field.contactTypes.splice(index, 1)
		},

		toSnakeCase(text) {
			return text
				.toLowerCase()
				.replace(/[^a-z0-9]+/g, '_')
				.replace(/(^_|_$)/g, '')
		},

		updateContactType(field, index, value) {
			if (!field.contactTypes[index]) return
			field.contactTypes[index].type = this.toSnakeCase(value)
		},
	},
	watch: {},
}
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style>
.multiselect__tag {
	padding: 5px 20px 5px 5px !important;
	margin-top: 0px !important;
	margin-bottom: 0px;
	max-height: 23px;
	min-height: 23px;
}

.multiselect__tags {
	display: block;
	width: 100%;
	padding: 0.375rem 0.75rem;
	font-size: 0.8rem;
	font-weight: 400;
	line-height: 1.6;
	color: #212529;
	background-color: #f8fafc;
	background-clip: padding-box;
	border: 1px solid #ced4da;
	-webkit-appearance: none;
	-moz-appearance: none;
	appearance: none;
	border-radius: 0.5rem;
	transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
	box-shadow: inset 0 -1px 0 #ddd;
	transition: box-shadow 0.2s;
	min-height: 23px;
}

.multiselect__placeholder {
	color: #adadad;
	display: inline-block;
	margin-bottom: 0px;
	padding-top: 2px;
}

.multiselect__tags-wrap {
	display: inline;
}
</style>
